// AdminTestDetails.jsx - Ana component güncellenmiş versiyon
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Container, IconButton, CircularProgress, Paper, Button } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector, useDispatch } from "react-redux";

import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import TestInfoCard from "./TestInfoCart";
import QuestionsList from "./QuestionList";
import AddQuestionDialog from "./AddQuestionDialog";
import EditTestDialog from "./EditTestDialog";
import EditQuestionDialog from "./EditQuestionDialog";
import DeleteQuestionDialog from "./DeleteQuestionDialog";

import { 
  getTestById, 
  clearCurrentTest,
  clearError,
  resetTestStates
} from "../../redux/testSlice";

const AdminTestDetails = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Dialog states
  const [dialogs, setDialogs] = useState({
    addQuestion: false,
    editTest: false,
    editQuestion: false,
    deleteQuestion: false
  });
  
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  const testsState = useSelector((state) => state.tests);
  const { 
    currentTest = null, 
    currentTestQuestions = [], 
    loading = false, 
    error = null, 
    addQuestionSuccess = false,
    updateTestSuccess = false,
    updateQuestionSuccess = false,
    deleteQuestionSuccess = false
  } = testsState || {};

  const questionNumbers = useMemo(() => {
    const unique = new Set();
    currentTestQuestions.forEach((q) => {
      if (q?.questionNumber != null) unique.add(q.questionNumber);
    });
    return Array.from(unique);
  }, [currentTestQuestions]);

  // Effects
  useEffect(() => {
    if (testId) {
      dispatch(getTestById(testId));
    }
    return () => {
      dispatch(clearCurrentTest());
    };
  }, [testId, dispatch]);

  // Success handlers with improved feedback
  useEffect(() => {
    if (addQuestionSuccess) {
      toast.success("Question added successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDialog('addQuestion');
      dispatch(resetTestStates());
      // Refresh data
      dispatch(getTestById(testId));
    }
  }, [addQuestionSuccess, testId, dispatch]);

  useEffect(() => {
    if (updateTestSuccess) {
      toast.success("Test updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDialog('editTest');
      dispatch(resetTestStates());
      dispatch(getTestById(testId));
    }
  }, [updateTestSuccess, testId, dispatch]);

  useEffect(() => {
    if (updateQuestionSuccess) {
      toast.success("Question updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDialog('editQuestion');
      setSelectedQuestion(null);
      dispatch(resetTestStates());
      dispatch(getTestById(testId));
    }
  }, [updateQuestionSuccess, testId, dispatch]);

  useEffect(() => {
    if (deleteQuestionSuccess) {
      toast.success("Question deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDialog('deleteQuestion');
      setSelectedQuestion(null);
      dispatch(resetTestStates());
      dispatch(getTestById(testId));
    }
  }, [deleteQuestionSuccess, testId, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-right",
        autoClose: 5000,
      });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Dialog handlers
  const openDialog = (dialogName, question = null) => {
    setDialogs(prev => ({ ...prev, [dialogName]: true }));
    if (question) setSelectedQuestion(question);
  };

  const closeDialog = (dialogName) => {
    setDialogs(prev => ({ ...prev, [dialogName]: false }));
    if (dialogName === 'editQuestion' || dialogName === 'deleteQuestion') {
      setSelectedQuestion(null);
    }
  };

  const handleBackToTests = () => {
    navigate("/adminTests");
  };

  // Loading state
  if (loading && !currentTest) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <Box textAlign="center">
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Loading test details...
                </Typography>
              </Box>
            </Box>
          </Container>
        </div>
      </div>
    );
  }

  // Not found state
  if (!currentTest && !loading) {
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <Container maxWidth="xl">
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="error" gutterBottom>
                Test not found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                The test with the specified ID does not exist or has been deleted.
              </Typography>
              <Button variant="contained" onClick={handleBackToTests}>
                Back to Test List
              </Button>
            </Paper>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        <div className="md:shrink-0 md:mr-6">
          <AdminSideBar />
        </div>
        <div className="flex-1 p-4 md:p-6">
          <Container maxWidth="xl">
            {/* Header */}
            <Box display="flex" alignItems="center" mb={3}>
              <IconButton 
                onClick={handleBackToTests} 
                sx={{ mr: 2 }}
                color="primary"
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" fontWeight="600" color="primary">
                Test Management
              </Typography>
            </Box>

            {/* Test Information Card */}
            {currentTest && (
              <TestInfoCard 
                test={currentTest}
                questionsCount={currentTestQuestions.length}
                onAddQuestion={() => openDialog('addQuestion')}
                onEditTest={() => openDialog('editTest')}
              />
            )}

            {/* Questions List */}
            <QuestionsList 
              questions={currentTestQuestions}
              onEditQuestion={(question) => openDialog('editQuestion', question)}
              onDeleteQuestion={(question) => openDialog('deleteQuestion', question)}
              onAddQuestion={() => openDialog('addQuestion')}
            />
          </Container>
        </div>
      </div>

      {/* Dialogs */}
      <AddQuestionDialog
        open={dialogs.addQuestion}
        onClose={() => closeDialog('addQuestion')}
        testId={testId}
        questionNumber={currentTestQuestions.length + 1}
        loading={loading}
        questionNumbers={questionNumbers}
      />

      <EditTestDialog
        open={dialogs.editTest}
        onClose={() => closeDialog('editTest')}
        test={currentTest}
        testId={testId}
        loading={loading}
      />

      <EditQuestionDialog
        open={dialogs.editQuestion}
        onClose={() => closeDialog('editQuestion')}
        question={selectedQuestion}
        testId={testId}
        loading={loading}
        currentTestQuestions={currentTestQuestions}
      />

      <DeleteQuestionDialog
        open={dialogs.deleteQuestion}
        onClose={() => closeDialog('deleteQuestion')}
        question={selectedQuestion}
        testId={testId}
        loading={loading}
      />

      <ToastContainer 
        position="top-right" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default AdminTestDetails;
